/**
 * 🧱 Refactor BT Phase 3 — constructeurs de payload PUT (Éditer) / POST (Créer) des bons de travail.
 *
 * Étape 0 : extraction MÉCANIQUE. Le corps de chaque fonction est la copie caractère pour
 * caractère (hors indentation) du code qui vivait dans les dialogues :
 *   - `construirePayloadEditer` ← EditerBonTravailDialog.jsx L1334-1671 (HEAD Phase 2B)
 *   - `construirePayloadCreer`  ← CreerBonTravailDialog.jsx  L591-640  (HEAD Phase 2B)
 * Aucun changement de comportement ; les goldens `__tests__/golden/refactor_bt/*.json` figent
 * les payloads produits (refactor pur : bugs B-1…B-22 préservés).
 */
import {
  sanitizeEmployesAssignesAbsentLocked,
  sanitizeChefDatesAssigneesAbsentLocked,
  detecterEtCorrigerExpansionsSilencieuses,
  stripMaskedHeuresBudgeteesFields,
} from '../components/crm/bon-travail/btSavePayloadHelpers';  // relatif : Jest n'a pas l'alias '@/'

/**
 * Payload PUT d'Éditer. `originalAssignesRef` = ref React (`.current` lu au moment du save,
 * comme dans le code d'origine). Retourne le payload déjà passé par `stripMaskedHeuresBudgeteesFields`.
 */
/**
 * 🧱 Phase 4 — Lot E (B-27) : dédoublonnage par `employee_id` — première occurrence gagne,
 * `dates_assignees` fusionnées (union triée ; `[]` explicite conservé si les deux sont vides).
 */
export function dedoublonnerEmployesAssignes(liste) {
  if (!Array.isArray(liste)) return liste;
  const parId = new Map();
  for (const e of liste) {
    const id = e?.employee_id || e?.employe_id;
    if (!id) { parId.set(Symbol(), e); continue; }
    const deja = parId.get(id);
    if (!deja) { parId.set(id, e); continue; }
    const a = Array.isArray(deja.dates_assignees) ? deja.dates_assignees : null;
    const b = Array.isArray(e.dates_assignees) ? e.dates_assignees : null;
    const fusion = (a || b) ? [...new Set([...(a || []), ...(b || [])])].sort() : deja.dates_assignees;
    parId.set(id, { ...deja, dates_assignees: fusion });
  }
  return [...parId.values()];
}

export function construirePayloadEditer({
  bon, form, employees, employesOriginal, membresExtras, membresExclus,
  lockedInfo, originalAssignesRef, extrasRetiresManuellement, isExt,
}) {
  // 🆕 2026-05 : si chefs_par_jour défini, calcul du chef principal (le + de jours)
  const cpj = form.chefs_par_jour || {};
  let principalChefId = form.chef_equipe_id || null;
  let principalChefNom = form.chef_equipe_nom || null;
  if (Object.keys(cpj).length > 0) {
    const counts = {};
    // 🆕 2026-05-08 — "__none__" est un sentinelle (Aucun chef ce jour) à ignorer dans le décompte
    Object.values(cpj).forEach(id => { if (id && id !== '__none__') counts[id] = (counts[id] || 0) + 1; });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (sorted.length > 0) {
      principalChefId = sorted[0][0];
      const emp = (employees || []).find(e => e.id === principalChefId);
      principalChefNom = emp ? `${emp.prenom} ${emp.nom}` : principalChefNom;
    }
  }
  // Construit employes_assignes selon le mode (chef principal vs chefs_par_jour)
  let employesAssignesPayload;
  if (Object.keys(cpj).length > 0) {
    // Mode multi-chefs : utilise form.employes_assignes tel quel (déjà rempli par setChefDuJour)
    // + ajouter les extras manuels si pas déjà présents
    // 🐛 2026-06-08 fix Bug 1 Phase 51 — exclure les spécialistes lavage à pression
    // (filtre déjà appliqué côté affichage des "membresExtras" ; sécurité défensive ici).
    const seen = new Set((form.employes_assignes || []).map(e => e.employee_id));
    const extras = membresExtras
      .filter(e => !seen.has(e.id))
      .map(e => {
        const existing = (form.employes_assignes || []).find(x => x.employee_id === e.id);
        return {
          employee_id: e.id,
          employee_nom: `${e.prenom} ${e.nom}`,
          role: 'extra',
          // 🛡️ [Phase 57 — v11] PRÉSERVE [] explicite (décochage volontaire).
          // Réf : /app/backend/lib/bt_equipe_normalize.py + audit Phase 57.
          dates_assignees: Array.isArray(existing?.dates_assignees)
            ? existing.dates_assignees
            : (form.dates_chantier || []),
        };
      });
    employesAssignesPayload = [
      ...(form.employes_assignes || []),
      ...extras,
    ];
  } else if (principalChefId) {
    // Mode legacy : chef principal + ses peintres + extras
    employesAssignesPayload = [
      {
        employee_id: principalChefId,
        role: 'chef_equipe',
        nom: principalChefNom,
        // 🛡️ [Phase 58 — 2026-06-14] PRÉSERVE [] explicite pour chef_dates_assignees.
        // Si le chef a été retiré de toutes les dates volontairement, ne PAS écraser.
        dates_assignees: Array.isArray(form.chef_dates_assignees)
          ? form.chef_dates_assignees
          : (form.dates_chantier || []),
      },
      // 🧱 Phase 4 — Lot E (équipe fixe abolie) : plus d'auto-ajout des employés dont la fiche a
      //   `chef_equipe_id` = chef du BT. Le payload = chef + membres réellement présents (extras/matrice).
      ...membresExtras
        // 🗑️ [v13.60.1] Filtre `est_specialiste_lavage` retiré (P2 RCA).
        .map(e => {
        const existing = (form.employes_assignes || []).find(x => x.employee_id === e.id);
        // 🎨 [v13.65.3 — 2026-02-28] PRÉSERVATION SÉMANTIQUE UNIVERSELLE.
        //
        //   Historique : v13.65.2 avait mis en place une whitelist restrictive
        //   `(originalRole === 'peintre' || originalRole === 'extra')` — trop
        //   étroite. Le testeur a détecté que 'installateur' (et par
        //   extension tout autre rôle légitime : 'sous_traitant',
        //   'representante', 'terrain', etc.) était encore dégradé en 'extra'.
        //
        //   Règle GÉNÉRALE (utilisatrice — 2026-02-28) :
        //     Préserver TOUT `originalRole` non-vide, avec UNE seule
        //     exception documentée : `'chef_equipe'` → 'extra'.
        //     Motif de l'exception : invariant Phase 91 — un seul chef
        //     principal, matérialisé via `chef_equipe_id` + l'entrée
        //     dédiée du chef construite L1337. Toute entrée orpheline
        //     `role='chef_equipe'` doit être neutralisée pour éviter les
        //     doubles chef et garantir la cohérence avec la migration
        //     Phase 91 auto-startup.
        //     Fallback 'extra' uniquement quand `originalRole` est
        //     absent/undefined (vrai NOUVEL ajout via handleQuickAddEmploye
        //     ou membresExtras synthétique sans historique).
        const originalRole = existing?.role;
        const preservedRole = (originalRole && originalRole !== 'chef_equipe')
          ? originalRole
          : 'extra';
        return {
          employee_id: e.id,
          role: preservedRole,
          nom: `${e.prenom} ${e.nom}`,
          // 🛡️ [Phase 57 — v11] PRÉSERVE [] explicite (décochage volontaire).
          dates_assignees: Array.isArray(existing?.dates_assignees)
            ? existing.dates_assignees
            : (form.dates_chantier || []),
        };
      }),
    ];
  } else {
    // BT sans chef principal et sans chefs_par_jour : membres explicites + extras
    // 🆕 [Phase 26 P0] Bug Stéphanie : avant, `membresExtras` était ignoré ici → l'employé
    //   ajouté via « Ajouter d'une autre équipe » disparaissait silencieusement au save.
    // 🐛 2026-06-08 fix Bug 1 Phase 51 — exclure les spécialistes lavage à pression
    // 🆕 [Phase 91 — 2026-07-07] BT sans chef : force role='extra' sur TOUS les employés
    //   du payload (chef orphelin ou membres) pour garantir la cohérence DB.
    //   ⚠️ Ce forçage reste OBLIGATOIRE pour cette branche (BT sans chef) — la
    //   migration Phase 91 (server.py L2793) nettoie tout `chef_equipe` orphelin
    //   sur BT `chef_equipe_id=null` en `extra`. Ne PAS préserver 'peintre' ici,
    //   sinon l'invariant est cassé au prochain boot par la migration.
    const seen = new Set();
    const combined = [];
    for (const ea of (form.employes_assignes || [])) {
      if (!ea.employee_id || seen.has(ea.employee_id)) continue;
      seen.add(ea.employee_id);
      combined.push({ ...ea, role: 'extra' });   // 🆕 Force role='extra' Phase 91
    }
    for (const e of membresExtras) {
      if (seen.has(e.id)) continue;  // 🗑️ [v13.60.1] Filtre est_specialiste_lavage retiré
      seen.add(e.id);
      combined.push({
        employee_id: e.id,
        role: 'extra',
        nom: `${e.prenom} ${e.nom}`,
        dates_assignees: form.dates_chantier || [],
      });
    }
    employesAssignesPayload = combined;
  }

  // 🛡️ [Phase 76 — 2026-06-21] SANITIZATION absent-locked via helper PUR
  //   testé par Jest (17 tests dans __tests__/btSavePayloadHelpers.phase76.test.js).
  //
  //   Bug 4e occurrence (réglé ici) : avant Phase 76, le code matérialisait
  //   `dates_chantier - absent` quand `dates_assignees=[]` + un settled_date
  //   existait → l'employé décoché ressuscitait sur les dates futures.
  //   Le helper PRÉSERVE strictement [] explicite (intention utilisatrice).
  const absentPerEmp = lockedInfo?.absent_per_employee || {};
  employesAssignesPayload = sanitizeEmployesAssignesAbsentLocked(
    employesAssignesPayload, absentPerEmp, form.dates_chantier
  );

  // 🛡️ [v13.65.1 — 2026-02-28] SAVE NON-DESTRUCTIF (règle de fusion stricte).
  //   Ré-injecte UNIQUEMENT les employés du BT original qui :
  //     (1) ne sont pas déjà présents dans le payload construit par l'UI
  //         (les sélections UI font FOI pour tout ID qu'elles couvrent), ET
  //     (2) sont VRAIMENT invisibles dans le picker filtré (aucun match
  //         dans `employees` — installateur/sous_traitant post-fusion,
  //         employee_id orphelin, etc.).
  //
  //   🚫 [v13.65.5 — 2026-02-28] EXCEPTION INSTALLATEUR (règle métier
  //     utilisatrice) : les installateurs ne doivent JAMAIS apparaître sur
  //     un BT. On les SKIP explicitement de la préservation, même s'ils
  //     étaient présents dans `bon.employes_assignes` (BT legacy). Le
  //     backend applique aussi ce filtre (défense en profondeur).
  //     Détection : (a) `role === 'installateur'` dans l'entrée originale,
  //     (b) l'employé lié dans `employesOriginal` (unfiltered) a
  //     role_terrain='installateur' ou type_employe='installateur'.
  //     Ref : /app/memory/REGLES_PLATEFORME.md § Règle 3.
  //
  //   Calcul DYNAMIQUE au moment du save : `employees` peut avoir été vide
  //   au mount (chargement async parent) et être maintenant peuplé. Utilise
  //   toujours la valeur COURANTE de `employees` pour trancher.
  //
  //   Ref : /app/memory/RCA_V13_65_PRESENCE_PAR_JOUR.md § Cause #2 + fix v13.65.1
  const originalAssignes = originalAssignesRef.current || [];
  if (originalAssignes.length > 0) {
    const seenPayloadIds = new Set(
      (employesAssignesPayload || [])
        .map(e => e?.employee_id)
        .filter(Boolean)
    );
    const visibleIds = new Set(
      (employees || []).map(e => e?.id).filter(Boolean)
    );
    // 🚫 [v13.65.5] Set des IDs des installateurs connus (source: employesOriginal
    //   unfiltered — cf. `filterEmployeesForBT` L44 qui les exclut du picker).
    const installateurIds = new Set(
      (employesOriginal || [])
        .filter(e => {
          const rt = String(e?.role_terrain || '').toLowerCase().trim();
          const te = String(e?.type_employe || '').toLowerCase().trim();
          return rt === 'installateur' || te === 'installateur';
        })
        .map(e => e?.id)
        .filter(Boolean)
    );
    for (const orig of originalAssignes) {
      const eid = orig?.employee_id;
      if (!eid) continue;
      // Règle (1) : payload UI fait foi si l'ID y est déjà.
      if (seenPayloadIds.has(eid)) continue;
      // Règle (2) : preserved comble UNIQUEMENT les invisibles.
      if (visibleIds.has(eid)) continue;
      // 🚫 Règle (3) v13.65.5 : NE JAMAIS préserver un installateur.
      const origRole = String(orig?.role || '').toLowerCase().trim();
      if (origRole === 'installateur' || installateurIds.has(eid)) continue;
      employesAssignesPayload.push({ ...orig });
      seenPayloadIds.add(eid);
    }
  }

  let chefDatesAssigneesSanitized = form.chef_dates_assignees;
  if (principalChefId) {
    const chefAbsent = absentPerEmp[principalChefId] || [];
    chefDatesAssigneesSanitized = sanitizeChefDatesAssigneesAbsentLocked(
      chefDatesAssigneesSanitized, chefAbsent, form.dates_chantier
    );
  }
  // 🐛 2026-05-09 — Nacelle gérée EXCLUSIVEMENT par les champs `equipements_nacelle_*`.
  // L'ancien doublon dans `bon.equipements[]` est désormais supprimé du payload :
  // l'aperçu BT et le calendrier de production lisent uniquement `equipements_nacelle_dates`.
  const equipementsSynced = (form.equipements_selectionnes || []).filter(e =>
    !((e.equipement_nom || '').toLowerCase().includes('nacelle'))
  );

  const payload = {
    ...bon,
    description_travaux: form.description_travaux,
    notes_speciales: form.notes_speciales || null,
    heures_budgetees: parseFloat(form.heures_budgetees) || 0,
    heures_budgetees_ajustees_manuellement: form.heures_budgetees_ajustees_manuellement || false,
    heure_debut: form.heure_debut || null,
    // 🔄 B4 — Persistance dates_fixes
    dates_fixes: !!form.dates_fixes,
    jours_pluie: form.jours_pluie || false,
    pluie_date_debut: form.pluie_date_debut || null,
    pluie_date_fin: form.pluie_date_fin || null,
    // 🆕 2026-05-06 Module CCQ — régime du BT
    regime_ccq: form.regime_ccq === 'ccq' ? 'ccq' : 'hors_ccq',
    chef_equipe_id: principalChefId,
    chef_equipe_nom: principalChefNom,
    // 🆕 2026-04-30 — chef_dates_assignees pour la matrice de présence
    // 🔒 2026-05-14 — utilise la version sanitizée (absent-locked retirés)
    // 🛡️ [Phase 58 — 2026-06-14] PRÉSERVE [] explicite. Si le chef a été retiré
    // volontairement de toutes les dates, ne JAMAIS écraser par dates_chantier.
    chef_dates_assignees: Array.isArray(chefDatesAssigneesSanitized)
      ? chefDatesAssigneesSanitized
      : (form.dates_chantier || []),
    // 🆕 2026-05 — chefs_par_jour : chef différent par jour (override chef_equipe_id ce jour-là)
    chefs_par_jour: Object.keys(cpj).length > 0 ? cpj : null,
    employes_assignes: dedoublonnerEmployesAssignes(employesAssignesPayload),
    // 🛡️ [Phase 57 — v11] Mémoire DB des extras retirés explicitement.
    // Cette liste empêche la ré-injection automatique par le useEffect
    // "réalité gravée" au prochain chargement du dialog.
    // Réf : /app/memory/audit_auto_injection_employes_phase57.md
    employes_extras_retires_explicitement: Array.from(extrasRetiresManuellement),
    produits: form.produits_selectionnes.filter(p => p.produit_id).map(p => ({
      produit_id: p.produit_id, produit_nom: p.produit_nom, fini: p.fini,
      unite: p.unite, quantite: parseFloat(p.quantite) || 1, quantite_prevue: parseFloat(p.quantite_prevue) || 0,
      emplacement: p.emplacement,
      code_couleur: p.code_couleur || null, commande_initiale_faite: p.commande_initiale_faite || false,
    })),
    etapes: form.etapes.filter(e => e.description.trim()).map((e, i) => ({ 
      numero: i + 1, description: e.description, completee: e.completee || false,
      date_completion: e.date_completion, completee_par: e.completee_par
    })),
    zones: isExt ? form.zones.map((z, i) => ({ numero: i + 1, nom: z.nom, description: z.description || '', photo_url: z.photo_url })) : [],
    dates_chantier: form.dates_chantier,
    equipements: isExt ? equipementsSynced.map(e => ({
      equipement_id: e.equipement_id, equipement_nom: e.equipement_nom,
      emoji: e.emoji, dates_utilisation: e.dates_utilisation,
      nb_etages_utilises: e.nb_etages_utilises || 0,
      apporter_lavage: e.apporter_lavage || false,
      notes_lavage: e.notes_lavage || '',
      date_montage: e.date_montage || null,
      date_demontage: e.date_demontage || null,
      // ✅ ÉTAPE 5 : Nouveaux champs échafauds
      type: e.type || 'autre',
      type_etage: e.type_etage || null,
      dimensions: e.dimensions || null,
      remorque_montage: e.remorque_montage || 'bureau',
      remorque_demontage: e.remorque_demontage || 'bureau'
    })) : [],
    lavage_pression_a_faire: isExt ? form.lavage_pression_a_faire : false,
    nacelle_lavage_requise: isExt && form.lavage_pression_a_faire ? (form.nacelle_lavage_requise || false) : false,
    nacelle_retour: isExt && form.nacelle_lavage_requise ? (form.nacelle_retour || 'bureau') : null,
    nacelle_apport_timing: isExt && form.nacelle_lavage_requise ? (form.nacelle_apport_timing || 'jour_j') : null,
    echafauds_a_monter: isExt && form.lavage_pression_a_faire ? (form.echafauds_a_monter || false) : false,
    echafauds_remorque_retour: isExt && form.echafauds_a_monter ? (form.echafauds_remorque_retour || 'bureau') : null,
    echafauds_date_demontage: isExt && form.echafauds_a_monter ? (form.echafauds_date_demontage || null) : null,
    echafauds_utilises: isExt && form.echafauds_a_monter ? (form.echafauds_utilises || []) : [],  // ✅ NOUVEAU : Multi-types
    echafauds_tours_config: isExt && form.echafauds_a_monter ? (form.echafauds_tours_config || []) : [],  // ✅ NOUVEAU : Tours par zone
    // ✅ 2026-04-22 : Case "Nettoyer à l'échelle" n'était pas sauvegardée
    nettoyer_echelle: isExt && form.lavage_pression_a_faire ? (form.nettoyer_echelle || false) : false,
    // 🆕 2026-05 : Type d'échelle ("24" ou "32") quand "Nettoyer à l'échelle" est coché
    nettoyer_echelle_type_pieds: isExt && form.lavage_pression_a_faire && form.nettoyer_echelle ? (form.nettoyer_echelle_type_pieds || '24') : null,
    // Champs legacy (garder pour compatibilité avec anciens BT)
    type_etage_echafauds: isExt && form.echafauds_a_monter ? (form.type_etage_echafauds || null) : null,
    dimensions_etage_echafauds: isExt && form.echafauds_a_monter ? (form.dimensions_etage_echafauds || null) : null,
    nb_etages_utilises: isExt && form.echafauds_a_monter ? (form.nb_etages_utilises || 0) : 0,
    notes_lavage_pression: isExt && form.lavage_pression_a_faire ? (form.notes_lavage_pression || null) : null,
    date_lavage_prevu: isExt && form.lavage_pression_a_faire ? (form.date_lavage_prevu || null) : null,
    notes_importantes: form.notes_importantes || null,
    notes_importantes_timing: form.notes_importantes ? (form.notes_importantes_timing || 'debut_travaux') : null,
    // ✅ NOUVEAUX CHAMPS : Gestion avancée nacelle/échafauds (Phases 2-5)
    equipements_nacelle_requise: isExt ? (form.equipements_nacelle_requise || false) : false,
    equipements_nacelle_id: isExt && form.equipements_nacelle_requise ? form.equipements_nacelle_id : null,
    equipements_nacelle_dates: isExt && form.equipements_nacelle_requise ? (form.equipements_nacelle_dates || []) : [],
    equipements_nacelle_retour_type: isExt && form.equipements_nacelle_requise ? (form.equipements_nacelle_retour_type || 'bureau') : null,
    equipements_nacelle_client_livraison: isExt && form.equipements_nacelle_requise && form.equipements_nacelle_retour_type === 'autre_chantier' ? form.equipements_nacelle_client_livraison : null,
    // ✅ PHASE 1 & 2 : Livraison nacelle avancée
    equipements_nacelle_livraison_timing: isExt && form.equipements_nacelle_requise ? (form.equipements_nacelle_livraison_timing || 'jour_j') : null,
    equipements_nacelle_vehicule_livraison_id: isExt && form.equipements_nacelle_requise && form.equipements_nacelle_livraison_timing === 'veille' ? form.equipements_nacelle_vehicule_livraison_id : null,
    equipements_nacelle_vehicule_livraison_nom: isExt && form.equipements_nacelle_requise && form.equipements_nacelle_livraison_timing === 'veille' ? form.equipements_nacelle_vehicule_livraison_nom : null,
    equipements_echafauds_requis: isExt ? (form.equipements_echafauds_requis || false) : false,
    equipements_echafauds_utilises: isExt && form.equipements_echafauds_requis ? (form.equipements_echafauds_utilises || []) : [],
    equipements_echafauds_zones: isExt && form.equipements_echafauds_requis ? (form.equipements_echafauds_zones || []) : [],
  };

  // 🛡️ [Phase 76 — 2026-06-21] FILET FINAL anti-expansion silencieuse.
  //   Détecte les cas où le payload final aurait écrasé un `dates_assignees=[]`
  //   explicite du formulaire (ex: bug dans une étape de reconstruction L1075-1116
  //   ou setChefDuJour). Corrige automatiquement + log diagnostique.
  //   Garantie : ce qui est [] dans `form.employes_assignes` reste [] dans le PUT.
  const { payload: payloadCorrige, expansions } = detecterEtCorrigerExpansionsSilencieuses(
    payload.employes_assignes, form.employes_assignes
  );
  if (expansions.length > 0) {
    console.error('[PHASE 76] Expansion silencieuse détectée et corrigée :', expansions);
    payload.employes_assignes = payloadCorrige;
  }
  // 🛡️ [v13.62 — 2026-02-28] Défense en profondeur : retirer les champs
  //   `heures_budgetees_*=null` masqués par `apply_heures_visibility` (Phase 81).
  //   Sans ce strip, un PUT venant d'un chef_equipe (non-owner) échoue avec
  //   Pydantic 422 sur `heures_budgetees: float` non-Optional. Le backend
  //   pop ces champs de toute façon pour les non-owners (crm_bons_travail.py L2594).
  //   Ref : /app/memory/RCA_V13_62_PRESENCE_PAR_JOUR.md
  const putPayload = stripMaskedHeuresBudgeteesFields(payload);
  return putPayload;
}

/**
 * Payload POST de Créer (`type` = type de travaux, `soumissions` = liste du dossier).
 */
export function construirePayloadCreer({ form, employees, dossier, soumissions, type }) {
  // 🆕 2026-05 : si chefs_par_jour est défini, calcule chef principal = celui avec le + de jours
  let principalChefId = form.chef_equipe_id || null;
  let principalChefNom = form.chef_equipe_nom || null;
  const cpj = form.chefs_par_jour || {};
  if (Object.keys(cpj).length > 0) {
    const counts = {};
    Object.values(cpj).forEach(id => { if (id) counts[id] = (counts[id] || 0) + 1; });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (sorted.length > 0) {
      principalChefId = sorted[0][0];
      const emp = employees.find(e => e.id === principalChefId);
      principalChefNom = emp ? `${emp.prenom} ${emp.nom}` : principalChefNom;
    }
  }
  const payload = {
    dossier_id: dossier.id,
    soumission_id: soumissions[0]?.id || null,
    type_travaux: type,
    description_travaux: form.description_travaux,
    notes_speciales: form.notes_speciales || null,
    notes_importantes: form.notes_importantes || null,
    notes_importantes_timing: form.notes_importantes ? (form.notes_importantes_timing || 'debut_travaux') : null,
    heures_budgetees: parseFloat(form.heures_budgetees) || 0,
    heure_debut: form.heure_debut || null,
    // 🔄 B4 — Bouton "Dates fixes" : empêche le report dans le calendrier
    dates_fixes: !!form.dates_fixes,
    jours_pluie: form.jours_pluie || false,
    pluie_date_debut: form.jours_pluie && form.pluie_date_debut ? form.pluie_date_debut : null,
    pluie_date_fin: form.jours_pluie && form.pluie_date_fin ? form.pluie_date_fin : null,
    chef_equipe_id: principalChefId,
    chef_equipe_nom: principalChefNom,
    chef_dates_assignees: form.chef_dates_assignees && form.chef_dates_assignees.length > 0
      ? form.chef_dates_assignees
      : [...form.dates_chantier],
    chefs_par_jour: Object.keys(cpj).length > 0 ? cpj : null,
    produits: form.produits_selectionnes.filter(p => p.produit_id).map(p => ({
      produit_id: p.produit_id, produit_nom: p.produit_nom, fini: p.fini,
      unite: p.unite, quantite: parseFloat(p.quantite) || 1, quantite_prevue: parseFloat(p.quantite_prevue) || 0,
      emplacement: p.emplacement,
      code_couleur: p.code_couleur || null,
    })),
    etapes: form.etapes.filter(e => e.description.trim()).map((e, i) => ({ numero: i + 1, description: e.description })),
    zones: type === 'exterieur' ? form.zones.map((z, i) => ({ numero: i + 1, nom: z.nom, description: z.description || '', photo_url: z.photo_url })) : [],
    dates_chantier: form.dates_chantier,
    employes_assignes: dedoublonnerEmployesAssignes(form.employes_assignes),
    equipements: type === 'exterieur' ? form.equipements_selectionnes.map(e => ({
      equipement_id: e.equipement_id, equipement_nom: e.equipement_nom,
      emoji: e.emoji, dates_utilisation: e.dates_utilisation,
    })) : [],
  };
  return payload;
}
